import "ckeditor5/ckeditor5.css"

import { Suspense, createElement, memo, useMemo } from "react"
import type { NamedExoticComponent, ReactElement } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import isEqual from "lodash-es/isEqual"
import isObject from "lodash-es/isObject"
import isString from "lodash-es/isString"

import { useCSSInsertion } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { type TGetValueFromTranslatedObjectFunction, useGetValueFromTranslatedObject } from "@/hooks"
import { emptyCallback } from "@/shared/types/functions"

import { RichTextRendererFallbackSpinner as FallbackSpinner } from "./components"
import { useCkRendererStylesheetLink } from "./hooks"
import { richTextRenderers as renderers } from "./renderers"
import { ERichTextRendererMode, type TRichTextRendererProps } from "./rich-text-renderer-types"
import { type TRichRendererUtils, richTextRendererUtils } from "./rich-text-renderer-utils"

import "./rich-text-renderer-styles.scss"

const { isStringHTML, isStringMarkdown, isStringPlainText }: TRichRendererUtils = richTextRendererUtils

function propsChecker(
    { key: prevKey, isDisabled: prevDisabled }: TRichTextRendererProps,
    { key: nextKey, isDisabled: nextDisabled }: TRichTextRendererProps
): boolean {
    return isEqual(prevKey, nextKey) && isEqual(prevDisabled, nextDisabled)
}

const classNamesMap: { [K in ERichTextRendererMode]: string } = {
    [ERichTextRendererMode.Edit]: "edit-mode",
    [ERichTextRendererMode.View]: "view-mode"
}

/**
 * Component for rendering custom created content on admin side.
 * @param content string content to render. Either HTML string or Markdown string or plain string
 * @param fallbackLoader custom loader
 * @param mode based on mode, toolbar and editing is active
 * @param onChange 'Edit' mode only! used for content state management
 * @param isEditorForceSkipped boolean parameter to skip ckeditor
 * @param allowedHtmlTags list of allowed tags for basic renderer
 * @param withLegacyStyles backward compatibility prop for preventing styles artefacts
 * @param isDisabled 'Edit' mode only! used for disabling the input
 * @param cssConfig css classNames config for insertion
 * @return ReactElement
 */
const Component: NamedExoticComponent<TRichTextRendererProps> = memo(
    ({
        content,
        fallbackLoader = undefined,
        mode = ERichTextRendererMode.Edit,
        onChange = emptyCallback,
        isEditorForceSkipped = false,
        allowedHtmlTags = undefined,
        withLegacyStyles = false,
        isDisabled = false,
        cssConfig = {
            classNameIdentifier: String(),
            classNamesToOverride: String(),
            editorClassName: String()
        }
    }: TRichTextRendererProps): ReactElement => {
        const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

        const displayContent: string = useMemo(
            (): string =>
                isString(content) ? content : isObject(content) ? getValueFromTranslatedObject(content) : String(),
            [content, getValueFromTranslatedObject]
        )

        useCSSInsertion({
            cssString: cssConfig.classNamesToOverride,
            isEnabled:
                !isEmpty(cssConfig.classNameIdentifier) &&
                !isEmpty(cssConfig.classNamesToOverride) &&
                eq(mode, ERichTextRendererMode.View)
        })

        function renderContent(): ReactElement {
            let element: ReactElement = null

            if (eq(mode, ERichTextRendererMode.Edit) || (isStringHTML(displayContent) && !isEditorForceSkipped)) {
                element = createElement(renderers.HTMLRenderer, {
                    content: displayContent,
                    isDisabled,
                    onChange,
                    mode
                })
                return element
            }
            if (isStringHTML(displayContent) && isEditorForceSkipped) {
                element = <renderers.BasicRenderer content={displayContent} allowedTags={allowedHtmlTags} />
            }
            if (isStringMarkdown(displayContent)) {
                element = <renderers.MarkdownRenderer content={displayContent} />
            }
            if (isStringPlainText(displayContent)) {
                element = <renderers.BasicRenderer content={displayContent} allowedTags={allowedHtmlTags} />
            }

            return element
        }

        useCkRendererStylesheetLink()

        return (
            <Suspense fallback={fallbackLoader || <FallbackSpinner className="my-3" />}>
                <div
                    className={buildCssClassName(
                        "rich-text-renderer",
                        mode === ERichTextRendererMode.Edit
                            ? classNamesMap[mode]
                            : eq(mode, ERichTextRendererMode.View) && !withLegacyStyles
                              ? classNamesMap[mode]
                              : eq(mode, ERichTextRendererMode.View) && withLegacyStyles && "view-mode-legacy",
                        {
                            [cssConfig.classNameIdentifier]: cssConfig.classNameIdentifier,
                            [cssConfig.editorClassName]: cssConfig.editorClassName
                        }
                    )}
                >
                    {renderContent()}
                </div>
            </Suspense>
        )
    },
    propsChecker
)

Component.displayName = "RickTextRenderer"

export { Component as RichTextRenderer }
